import MarkerClusterGroup from '@changey/react-leaflet-markercluster';
import L from 'leaflet';
import { useMemo } from 'react';
import { Marker, Tooltip } from 'react-leaflet';
import { temperatureColorScale } from 'utils/colors/colorScale.utils';
import { CURRENT_CONDITIONS_CLUSTER_RADIUS } from '../../../../config/currentConditions.config';
import {
  formatCurrentConditionTemperature,
  getCurrentConditionKey,
  getCurrentConditionPosition,
  getCurrentConditionTemperature,
  getMapReadyCurrentConditions,
  getTemperatureStatistics,
} from '../../../../utils/currentConditions.utils';
import CurrentConditionMarkerContent from '../CurrentConditionMarkerContent/CurrentConditionMarkerContent';

const emptyMarkerIcon = L.divIcon({
  className: 'current-conditions-empty-icon',
  iconAnchor: [0, 0],
  iconSize: [0, 0],
});

const createClusterIcon = (cluster) => {
  const markers = cluster.getAllChildMarkers();
  const { meanTemperature, observationCount } = getTemperatureStatistics(
    markers.map((marker) => marker.options.temperature),
  );
  const locationCount = cluster.getChildCount();
  const formattedMeanTemperature = formatCurrentConditionTemperature(meanTemperature);
  const temperatureLabel =
    formattedMeanTemperature === null ? 'Unavailable' : `Avg. ${formattedMeanTemperature}°F`;
  const accessibleTemperature =
    formattedMeanTemperature === null
      ? `Temperature unavailable for all ${locationCount} locations`
      : `Mean temperature ${formattedMeanTemperature} degrees Fahrenheit from ${observationCount} valid observations across ${locationCount} locations`;
  const temperatureColor =
    meanTemperature === null ? '#ffffff' : temperatureColorScale(meanTemperature);
  const observationLabel =
    observationCount === 0
      ? 'No temperature data'
      : `${observationCount} ${observationCount === 1 ? 'station' : 'stations'}`;

  return L.divIcon({
    className: 'current-conditions-empty-icon',
    html: `<div class="current-conditions-cluster-content" role="img" aria-label="${accessibleTemperature}" style="--current-conditions-cluster-color: ${temperatureColor}">
      <span class="current-conditions-cluster-temperature">${temperatureLabel}</span>
      <span class="current-conditions-cluster-label">${observationLabel}</span>
    </div>`,
    iconAnchor: [0, 0],
    iconSize: [0, 0],
  });
};

const CurrentConditionsMapLayers = ({ conditions = [] }) => {
  // Memoized so marker position/key references stay stable between renders;
  // react-leaflet compares positions by reference and calls setLatLng on every change.
  const mapReadyConditions = useMemo(() => {
    const keyOccurrences = new Map();

    return getMapReadyCurrentConditions(conditions)
      .map((condition) => {
        const position = getCurrentConditionPosition(condition);
        const baseKey = getCurrentConditionKey(condition, position);

        if (!baseKey) {
          return null;
        }

        const occurrence = keyOccurrences.get(baseKey) ?? 0;
        keyOccurrences.set(baseKey, occurrence + 1);

        return {
          condition,
          key: occurrence === 0 ? baseKey : `${baseKey}-duplicate-${occurrence}`,
          position,
          temperature: getCurrentConditionTemperature(condition),
        };
      })
      .filter(Boolean);
  }, [conditions]);

  return (
    <MarkerClusterGroup
      iconCreateFunction={createClusterIcon}
      maxClusterRadius={CURRENT_CONDITIONS_CLUSTER_RADIUS}
    >
      {mapReadyConditions.map(({ condition, key, position, temperature }) => (
        // temperature is part of the key: react-leaflet only updates a fixed set of Marker
        // options after creation, so a changed reading must recreate the marker for
        // createClusterIcon (which reads marker.options.temperature) to see the new value.
        <Marker
          icon={emptyMarkerIcon}
          key={`${key}-${temperature}`}
          position={position}
          temperature={temperature}
        >
          <Tooltip className="current-conditions-tooltip" direction="center" permanent>
            <CurrentConditionMarkerContent condition={condition} />
          </Tooltip>
        </Marker>
      ))}
    </MarkerClusterGroup>
  );
};

export default CurrentConditionsMapLayers;
